/**
 * Fixed Route Shift Allocator
 * 
 * Allocates vehicle route assignments into compliant shifts with break scheduling.
 * Organizes route assignments into shifts that respect maximum shift time constraints,
 * schedules breaks according to regulatory requirements, and handles route assignment
 * splitting when needed.
 */

import type { VehicleAssignment, RouteAssignmentTime } from './fleetTypes';
import type { GeneratorConfig } from '../../components/map/components/auto-fleet-generator/types';
import type { BreakPeriod } from '../../types/simulation';
import { 
  extractShiftConfig, 
  calculateRequiredBreaks,
  scheduleBreaks,
  findStaggeredBreakTimes,
  type ShiftConfig
} from './shiftUtilities';
import { parseTime, calculateTimeDifference, formatTime, addMinutes } from '../../components/map/components/auto-fleet-generator/timeUtils';
import { calculateRouteLoopDuration } from './vehicleCalculator';
import { addMinutesToTime } from './timetableUtils';

/**
 * FixedRouteShiftAllocator class
 * 
 * Takes vehicle assignments with route assignments and organizes them into
 * compliant shifts with break scheduling.
 */
export class FixedRouteShiftAllocator {
  private config: ShiftConfig;
  private staggerBreaks: boolean;
  private trafficMultiplier: number;

  /**
   * Create a new FixedRouteShiftAllocator instance
   * 
   * @param assignments - Initial vehicle assignments (can be empty, will be set in allocateShifts)
   * @param generatorConfig - Generator configuration including shift parameters
   */
  constructor(
    assignments: VehicleAssignment[],
    generatorConfig: GeneratorConfig
  ) {
    this.config = extractShiftConfig(generatorConfig);
    this.staggerBreaks = generatorConfig.staggerBreaks ?? true;
    // TODO: Get traffic multiplier from config or assignments
    // For now, default to 1.0
    this.trafficMultiplier = 1.0;
  }

  /**
   * Allocate shifts for vehicle assignments
   * 
   * Main entry point: takes vehicle assignments and returns updated assignments
   * with route assignments organized into shifts respecting max shift time constraints.
   * If a vehicle needs multiple shifts, it will be split into multiple VehicleAssignments.
   * 
   * @param assignments - Vehicle assignments to allocate shifts for
   * @returns Updated vehicle assignments with shifts organized (may have more vehicles than input if split)
   */
  allocateShifts(assignments: VehicleAssignment[]): VehicleAssignment[] {
    const result: VehicleAssignment[] = [];
    
    assignments.forEach((assignment, vehicleIndex) => {
      const shiftAssignments = this.allocateShiftsForVehicle(assignment, vehicleIndex);
      
      // Validate shifts don't overlap for same vehicle (5.1.3.3)
      if (shiftAssignments.length > 1) {
        const periods = shiftAssignments.map(sa => ({
          startTime: sa.operatingPeriod.startTime,
          endTime: sa.operatingPeriod.endTime,
        }));
        
        if (!this.validateShiftsDontOverlap(periods)) {
          throw new Error(
            `Shifts overlap for vehicle ${assignment.vehicleName}. ` +
            `This indicates a scheduling error in shift allocation.`
          );
        }
      }
      
      result.push(...shiftAssignments);
    });
    
    return result;
  }

  /**
   * Allocate shifts for a single vehicle
   * 
   * Extended to handle mixed service types (fixed routes + on-demand)
   * 
   * @param assignment - Vehicle assignment to process (may include on-demand periods)
   * @param vehicleIndex - Index of the vehicle (0-based) for staggering
   * @returns Array of updated assignments (one per shift if multiple shifts needed)
   */
  private allocateShiftsForVehicle(
    assignment: VehicleAssignment & {
      onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
    },
    vehicleIndex: number
  ): VehicleAssignment[] {
    // Check if this is a mixed assignment (has both fixed routes and on-demand)
    const hasFixedRoutes = assignment.routeAssignments.length > 0;
    const hasOnDemand = assignment.onDemandPeriods && assignment.onDemandPeriods.length > 0;
    
    if (hasFixedRoutes && hasOnDemand) {
      // Mixed assignment: handle both service types together
      return this.allocateMixedShiftsForVehicle(assignment, vehicleIndex);
    } else if (hasOnDemand) {
      // On-demand only: handle with unified break scheduling
      return this.allocateOnDemandShiftsForVehicle(assignment, vehicleIndex);
    } else {
      // Fixed routes only: use existing logic
      return this.allocateFixedRouteShiftsForVehicle(assignment, vehicleIndex);
    }
  }

  /**
   * Allocate shifts for mixed assignment (fixed routes + on-demand)
   * 
   * @param assignment - Mixed vehicle assignment
   * @param vehicleIndex - Index for staggering
   * @returns Array of shift assignments
   */
  private allocateMixedShiftsForVehicle(
    assignment: VehicleAssignment & {
      onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
    },
    vehicleIndex: number
  ): VehicleAssignment[] {
    // Consolidate all periods (fixed route and on-demand) into shifts
    const consolidated = this.consolidateMixedAssignmentsIntoShifts(assignment);
    
    // Create shift periods with breaks across all service types
    const shiftPeriods = this.createMixedShiftPeriods(assignment, consolidated, vehicleIndex);
    
    return shiftPeriods;
  }

  /**
   * Allocate shifts for on-demand only assignment
   * 
   * @param assignment - On-demand vehicle assignment
   * @param vehicleIndex - Index for staggering
   * @returns Array of shift assignments
   */
  private allocateOnDemandShiftsForVehicle(
    assignment: VehicleAssignment & {
      onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
    },
    vehicleIndex: number
  ): VehicleAssignment[] {
    // For on-demand only, shifts are already created by ShiftGenerator
    // Just need to ensure breaks are scheduled uniformly
    const onDemandPeriods = assignment.onDemandPeriods || [];
    
    if (onDemandPeriods.length === 0) {
      return [assignment];
    }
    
    // Group on-demand periods into shifts based on max shift time
    const shiftGroups = this.groupOnDemandPeriodsIntoShifts(onDemandPeriods);
    
    return shiftGroups.map((periods, index) => {
      // Find earliest start and latest end
      let earliestStart: string | null = null;
      let latestEnd: string | null = null;
      
      periods.forEach((period) => {
        if (!earliestStart || this.compareTimes(period.start, earliestStart) < 0) {
          earliestStart = period.start;
        }
        if (!latestEnd || this.compareTimes(period.end, latestEnd) > 0) {
          latestEnd = period.end;
        }
      });
      
      // Schedule breaks uniformly across the shift
      const shiftStart = parseTime(earliestStart!);
      const shiftEnd = parseTime(latestEnd!);
      const shiftDuration = calculateTimeDifference(shiftStart, shiftEnd);
      const requiredBreaks = calculateRequiredBreaks(shiftDuration, shiftStart, this.config);
      
      const breaks = this.staggerBreaks
        ? findStaggeredBreakTimes(
            shiftStart,
            shiftDuration,
            vehicleIndex + index,
            this.config
          )
        : scheduleBreaks(shiftStart, shiftEnd, requiredBreaks, this.config);
      
      return {
        ...assignment,
        vehicleId: `${assignment.vehicleId}-shift-${index + 1}`,
        vehicleName: `${assignment.vehicleName} - Shift ${index + 1}`,
        routeAssignments: [],
        operatingPeriod: {
          startTime: earliestStart!,
          endTime: latestEnd!,
        },
        onDemandPeriods: periods,
        shiftBreaks: breaks.map(b => ({
          start: formatTime(b.startMinutes),
          end: formatTime(b.endMinutes),
          location: '',
        })),
      } as VehicleAssignment & {
        onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
        shiftBreaks?: Array<{ start: string; end: string; location: string }>;
      };
    });
  }

  /**
   * Allocate shifts for fixed route only assignment (existing logic)
   */
  private allocateFixedRouteShiftsForVehicle(
    assignment: VehicleAssignment,
    vehicleIndex: number
  ): VehicleAssignment[] {
    // Consolidate route assignments into shift groups
    const consolidated = this.consolidateAssignmentsIntoShifts(assignment);
    
    // Create shift periods with breaks
    const shiftPeriods = this.createShiftPeriods(assignment, consolidated, vehicleIndex);
    
    return shiftPeriods;
  }

  /**
   * Consolidate mixed assignments (fixed routes + on-demand) into shifts
   * 
   * Groups both fixed route assignments and on-demand periods into shifts
   * based on maxShiftTime constraint, respecting transitions between service types.
   * 
   * @param assignment - Mixed vehicle assignment
   * @returns Array of shift groups, each containing periods for one shift
   */
  private consolidateMixedAssignmentsIntoShifts(
    assignment: VehicleAssignment & {
      onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
    }
  ): Array<{
    fixedRouteAssignments: RouteAssignmentTime[];
    onDemandPeriods: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
  }> {
    const breakTime = this.config.breakLengthMinutes;
    const minTimeBefore = this.config.minTimeBeforeBreakMinutes;
    const minTimeAfter = this.config.minTimeAfterBreakMinutes;
    const availableShiftTime = this.config.maxShiftTimeMinutes - (breakTime + minTimeBefore + minTimeAfter);

    // Collect all periods (fixed route and on-demand) sorted by start time
    const allPeriods: Array<{
      type: 'fixed' | 'on-demand';
      start: string;
      end: string;
      fixedAssignment?: RouteAssignmentTime;
      onDemandPeriod?: { start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' };
    }> = [];

    // Add fixed route assignments
    assignment.routeAssignments.forEach((routeAssignment) => {
      allPeriods.push({
        type: 'fixed',
        start: routeAssignment.startTime,
        end: routeAssignment.endTime,
        fixedAssignment: routeAssignment,
      });
    });

    // Add on-demand periods
    const onDemandPeriods = assignment.onDemandPeriods || [];
    onDemandPeriods.forEach((period) => {
      allPeriods.push({
        type: 'on-demand',
        start: period.start,
        end: period.end,
        onDemandPeriod: period,
      });
    });

    // Sort by start time
    allPeriods.sort((a, b) => {
      const aStart = parseTime(a.start);
      const bStart = parseTime(b.start);
      return aStart - bStart;
    });

    // Group into shifts
    const shiftGroups: Array<{
      fixedRouteAssignments: RouteAssignmentTime[];
      onDemandPeriods: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
    }> = [];

    let currentShift = {
      fixedRouteAssignments: [] as RouteAssignmentTime[],
      onDemandPeriods: [] as Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>,
    };

    let currentShiftDuration = 0;
    let currentShiftEndTime: number | null = null;

    for (const period of allPeriods) {
      const start = parseTime(period.start);
      const end = parseTime(period.end);
      const duration = calculateTimeDifference(start, end);

      // Check if this period fits in current shift
      if (currentShiftDuration + duration <= availableShiftTime &&
          (currentShiftEndTime === null || start >= currentShiftEndTime)) {
        // Fits: add to current shift
        if (period.type === 'fixed' && period.fixedAssignment) {
          currentShift.fixedRouteAssignments.push(period.fixedAssignment);
        } else if (period.type === 'on-demand' && period.onDemandPeriod) {
          currentShift.onDemandPeriods.push(period.onDemandPeriod);
        }
        currentShiftDuration += duration;
        currentShiftEndTime = end > (currentShiftEndTime || 0) ? end : currentShiftEndTime;
      } else {
        // Doesn't fit: start new shift
        if (currentShift.fixedRouteAssignments.length > 0 || currentShift.onDemandPeriods.length > 0) {
          shiftGroups.push(currentShift);
        }
        currentShift = {
          fixedRouteAssignments: [],
          onDemandPeriods: [],
        };
        currentShiftDuration = 0;
        currentShiftEndTime = null;

        // Check if this period exceeds available shift time and needs splitting
        if (period.type === 'fixed' && period.fixedAssignment && duration > availableShiftTime) {
          // Route assignment exceeds shift time - split if circulator
          if (period.fixedAssignment.route.isCirculator && period.fixedAssignment.loopNumbers && period.fixedAssignment.loopNumbers.length > 0) {
            // Circulator: split at loop boundaries using maxShiftTime (breaks scheduled within shift)
            const splitAssignments = this.splitCirculatorAtTerminus(period.fixedAssignment, this.config.maxShiftTimeMinutes);
            
            // Add first split to current shift
            if (splitAssignments.length > 0) {
              const firstSplit = splitAssignments[0];
              const firstDuration = calculateTimeDifference(
                parseTime(firstSplit.startTime),
                parseTime(firstSplit.endTime)
              );
              currentShift.fixedRouteAssignments.push(firstSplit);
              currentShiftDuration = firstDuration;
              currentShiftEndTime = parseTime(firstSplit.endTime);
              
              // Remaining splits each go into their own shifts
              for (let i = 1; i < splitAssignments.length; i++) {
                if (currentShift.fixedRouteAssignments.length > 0 || currentShift.onDemandPeriods.length > 0) {
                  shiftGroups.push(currentShift);
                }
                const splitAssignment = splitAssignments[i];
                const splitDuration = calculateTimeDifference(
                  parseTime(splitAssignment.startTime),
                  parseTime(splitAssignment.endTime)
                );
                currentShift = {
                  fixedRouteAssignments: [splitAssignment],
                  onDemandPeriods: [],
                };
                currentShiftDuration = splitDuration;
                currentShiftEndTime = parseTime(splitAssignment.endTime);
              }
            } else {
              // No splits possible - route too long, assign to own shift (will warn later)
              currentShift.fixedRouteAssignments.push(period.fixedAssignment);
              currentShiftDuration = duration;
              currentShiftEndTime = end;
            }
          } else {
            // Non-circulator: complete route goes in its own shift (routes never split)
            currentShift.fixedRouteAssignments.push(period.fixedAssignment);
            currentShiftDuration = duration;
            currentShiftEndTime = end;
          }
        } else {
          // Period fits in single shift or is on-demand
          if (period.type === 'fixed' && period.fixedAssignment) {
            currentShift.fixedRouteAssignments.push(period.fixedAssignment);
          } else if (period.type === 'on-demand' && period.onDemandPeriod) {
            currentShift.onDemandPeriods.push(period.onDemandPeriod);
          }
          currentShiftDuration = duration;
          currentShiftEndTime = end;
        }
      }
    }

    // Add final shift
    if (currentShift.fixedRouteAssignments.length > 0 || currentShift.onDemandPeriods.length > 0) {
      shiftGroups.push(currentShift);
    }

    return shiftGroups.length > 0 ? shiftGroups : [{
      fixedRouteAssignments: [],
      onDemandPeriods: [],
    }];
  }

  /**
   * Create mixed shift periods with breaks scheduled uniformly across service types
   * 
   * @param originalAssignment - Original mixed vehicle assignment
   * @param consolidatedAssignments - Mixed periods organized into shift groups
   * @param vehicleIndex - Vehicle index for break staggering
   * @returns Array of vehicle assignments (one per shift)
   */
  private createMixedShiftPeriods(
    originalAssignment: VehicleAssignment & {
      onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
    },
    consolidatedAssignments: Array<{
      fixedRouteAssignments: RouteAssignmentTime[];
      onDemandPeriods: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
    }>,
    vehicleIndex: number
  ): VehicleAssignment[] {
    const shiftAssignments: VehicleAssignment[] = [];

    for (let shiftIndex = 0; shiftIndex < consolidatedAssignments.length; shiftIndex++) {
      const shiftGroup = consolidatedAssignments[shiftIndex];

      // Find earliest start and latest end across all periods in shift
      let shiftStartMinutes: number | null = null;
      let shiftEndMinutes: number | null = null;

      // Check fixed route assignments
      shiftGroup.fixedRouteAssignments.forEach((routeAssignment) => {
        const start = parseTime(routeAssignment.startTime);
        const end = parseTime(routeAssignment.endTime);
        if (shiftStartMinutes === null || start < shiftStartMinutes) {
          shiftStartMinutes = start;
        }
        if (shiftEndMinutes === null || end > shiftEndMinutes) {
          shiftEndMinutes = end;
        }
      });

      // Check on-demand periods
      shiftGroup.onDemandPeriods.forEach((period) => {
        const start = parseTime(period.start);
        const end = parseTime(period.end);
        if (shiftStartMinutes === null || start < shiftStartMinutes) {
          shiftStartMinutes = start;
        }
        if (shiftEndMinutes === null || end > shiftEndMinutes) {
          shiftEndMinutes = end;
        }
      });

      if (shiftStartMinutes === null || shiftEndMinutes === null) {
        continue; // Skip empty shifts
      }

      // Calculate actual service time (sum of fixed route durations + on-demand durations, excluding gaps)
      let totalServiceTime = 0;
      
      // Add fixed route assignment durations
      shiftGroup.fixedRouteAssignments.forEach((routeAssignment) => {
        const start = parseTime(routeAssignment.startTime);
        const end = parseTime(routeAssignment.endTime);
        totalServiceTime += calculateTimeDifference(start, end);
      });
      
      // Add on-demand period durations
      shiftGroup.onDemandPeriods.forEach((period) => {
        const start = parseTime(period.start);
        const end = parseTime(period.end);
        totalServiceTime += calculateTimeDifference(start, end);
      });

      const shiftDuration = calculateTimeDifference(shiftStartMinutes, shiftEndMinutes);
      console.log(`[Shift Allocator] Mixed shift break calculation for shift ${shiftStartMinutes}-${shiftEndMinutes} (duration: ${shiftDuration} min):`);
      console.log(`  - Fixed route assignments: ${shiftGroup.fixedRouteAssignments.length}`);
      console.log(`  - On-demand periods: ${shiftGroup.onDemandPeriods.length}`);
      console.log(`  - Total service time: ${totalServiceTime} min`);
      console.log(`  - Gap time: ${shiftDuration - totalServiceTime} min`);
      console.log(`  - Using SERVICE TIME (${totalServiceTime} min) for break calculation instead of shift duration (${shiftDuration} min)`);

      // Calculate breaks based on SERVICE TIME, not shift duration (which includes gaps)
      const requiredBreaks = calculateRequiredBreaks(totalServiceTime, shiftStartMinutes, this.config);
      console.log(`  - Breaks required: ${requiredBreaks} (based on service time)`);

      // Schedule breaks uniformly across the entire shift (regardless of service type)
      // Use service time for break scheduling calculations
      let breaks: Array<{ startMinutes: number; endMinutes: number; durationMinutes: number }>;
      if (this.staggerBreaks) {
        breaks = findStaggeredBreakTimes(
          shiftStartMinutes,
          totalServiceTime, // Use service time, not shift duration
          vehicleIndex + shiftIndex,
          this.config
        );
      } else {
        // Calculate a virtual shift end based on service time for break scheduling
        const serviceBasedShiftEnd = shiftStartMinutes + totalServiceTime;
        breaks = scheduleBreaks(shiftStartMinutes, serviceBasedShiftEnd, requiredBreaks, this.config);
      }

      // Adjust breaks to fit between route assignments/periods and filter based on minimum time requirements
      // Combine fixed routes and on-demand periods for break adjustment
      const allAssignments = [
        ...shiftGroup.fixedRouteAssignments.map(ra => ({
          startTime: ra.startTime,
          endTime: ra.endTime,
          type: 'fixed' as const,
        })),
        ...shiftGroup.onDemandPeriods.map(p => ({
          startTime: p.start,
          endTime: p.end,
          type: 'on-demand' as const,
        })),
      ].sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime));

      // Filter breaks to only those that meet minimum time requirements based on service time
      breaks = this.filterBreaksByServiceTime(
        breaks, 
        allAssignments.map(a => ({ startTime: a.startTime, endTime: a.endTime }))
      );
      
      console.log(`[Shift Allocator] After filtering: ${breaks.length} break(s) remain out of ${requiredBreaks} calculated`);

      // Create shift assignment
      const shiftAssignment: VehicleAssignment & {
        onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
        shiftBreaks?: Array<{ start: string; end: string; location: string }>;
      } = {
        vehicleId: consolidatedAssignments.length > 1
          ? `${originalAssignment.vehicleId}-shift-${shiftIndex + 1}`
          : originalAssignment.vehicleId,
        vehicleName: consolidatedAssignments.length > 1
          ? `${originalAssignment.vehicleName} - Shift ${shiftIndex + 1}`
          : originalAssignment.vehicleName,
        routeAssignments: shiftGroup.fixedRouteAssignments,
        operatingPeriod: {
          startTime: formatTime(shiftStartMinutes),
          endTime: formatTime(shiftEndMinutes),
        },
        onDemandPeriods: shiftGroup.onDemandPeriods,
        shiftBreaks: breaks.map(b => ({
          start: formatTime(b.startMinutes),
          end: formatTime(b.endMinutes),
          location: '',
        })),
      };

      shiftAssignments.push(shiftAssignment);
    }

    return shiftAssignments;
  }

  /**
   * Group on-demand periods into shifts based on max shift time
   * 
   * @param onDemandPeriods - Array of on-demand periods
   * @returns Array of period groups, each representing one shift
   */
  private groupOnDemandPeriodsIntoShifts(
    onDemandPeriods: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>
  ): Array<Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>> {
    const breakTime = this.config.breakLengthMinutes;
    const minTimeBefore = this.config.minTimeBeforeBreakMinutes;
    const minTimeAfter = this.config.minTimeAfterBreakMinutes;
    const availableShiftTime = this.config.maxShiftTimeMinutes - (breakTime + minTimeBefore + minTimeAfter);

    // Sort periods by start time
    const sortedPeriods = [...onDemandPeriods].sort((a, b) => {
      const aStart = parseTime(a.start);
      const bStart = parseTime(b.start);
      return aStart - bStart;
    });

    const shiftGroups: Array<Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>> = [];
    let currentShift: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }> = [];
    let currentShiftDuration = 0;
    let currentShiftEndTime: number | null = null;

    for (const period of sortedPeriods) {
      const start = parseTime(period.start);
      const end = parseTime(period.end);
      const duration = calculateTimeDifference(start, end);

      if (currentShiftDuration + duration <= availableShiftTime &&
          (currentShiftEndTime === null || start >= currentShiftEndTime)) {
        // Fits in current shift
        currentShift.push(period);
        currentShiftDuration += duration;
        currentShiftEndTime = end > (currentShiftEndTime || 0) ? end : currentShiftEndTime;
      } else {
        // Start new shift
        if (currentShift.length > 0) {
          shiftGroups.push(currentShift);
        }
        currentShift = [period];
        currentShiftDuration = duration;
        currentShiftEndTime = end;
      }
    }

    // Add final shift
    if (currentShift.length > 0) {
      shiftGroups.push(currentShift);
    }

    return shiftGroups.length > 0 ? shiftGroups : [sortedPeriods];
  }

  /**
   * Helper function to compare two time strings (HH:mm format)
   */
  private compareTimes(time1: string, time2: string): number {
    const [h1, m1] = time1.split(':').map(Number);
    const [h2, m2] = time2.split(':').map(Number);
    const minutes1 = h1 * 60 + m1;
    const minutes2 = h2 * 60 + m2;
    
    if (minutes1 < minutes2) return -1;
    if (minutes1 > minutes2) return 1;
    return 0;
  }

  /**
   * Calculate number of shifts needed for a vehicle
   * 
   * @param assignment - Vehicle assignment to analyze
   * @returns Number of shifts needed (1 or more)
   */
  calculateShiftsNeeded(assignment: VehicleAssignment): number {
    // Calculate total route assignment duration
    let totalDuration = 0;
    
    for (const routeAssignment of assignment.routeAssignments) {
      const start = parseTime(routeAssignment.startTime);
      const end = parseTime(routeAssignment.endTime);
      const duration = calculateTimeDifference(start, end);
      totalDuration += duration;
    }

    // Account for break time
    // Available shift time = maxShiftTime - (break time + min time before + min time after)
    const breakTime = this.config.breakLengthMinutes;
    const minTimeBefore = this.config.minTimeBeforeBreakMinutes;
    const minTimeAfter = this.config.minTimeAfterBreakMinutes;
    const availableShiftTime = this.config.maxShiftTimeMinutes - (breakTime + minTimeBefore + minTimeAfter);

    if (availableShiftTime <= 0) {
      // Edge case: shift time constraints are too tight
      return 1; // Will be handled by validation
    }

    if (totalDuration <= availableShiftTime) {
      return 1;
    }

    // Need multiple shifts
    return Math.ceil(totalDuration / availableShiftTime);
  }

  /**
   * Consolidate route assignments into shift groups
   * 
   * Groups route assignments into shifts based on maxShiftTime constraint.
   * Splits route assignments if they exceed shift duration.
   * 
   * @param assignment - Vehicle assignment to consolidate
   * @returns Array of shift groups, each containing route assignments for one shift
   */
  consolidateAssignmentsIntoShifts(assignment: VehicleAssignment): RouteAssignmentTime[][] {
    const breakTime = this.config.breakLengthMinutes;
    const minTimeBefore = this.config.minTimeBeforeBreakMinutes;
    const minTimeAfter = this.config.minTimeAfterBreakMinutes;
    const availableShiftTime = this.config.maxShiftTimeMinutes - (breakTime + minTimeBefore + minTimeAfter);

    // Sort route assignments by start time to handle them sequentially
    const sortedAssignments = [...assignment.routeAssignments].sort((a, b) => {
      const aStart = parseTime(a.startTime);
      const bStart = parseTime(b.startTime);
      return aStart - bStart;
    });

    const shiftGroups: RouteAssignmentTime[][] = [];
    let currentShift: RouteAssignmentTime[] = [];
    let currentShiftDuration = 0;
    let currentShiftEndTime: number | null = null; // Track when current shift ends

    for (const routeAssignment of sortedAssignments) {
      const start = parseTime(routeAssignment.startTime);
      const end = parseTime(routeAssignment.endTime);
      const duration = calculateTimeDifference(start, end);

      // Ensure routes are scheduled sequentially (no overlaps)
      // If this route starts before the current shift ends, we need a new shift
      let routeStartTime = start;
      if (currentShiftEndTime !== null && start < currentShiftEndTime) {
        // Route overlaps with current shift - start it after current shift ends
        routeStartTime = currentShiftEndTime;
        // Recalculate end time based on new start
        // For circulators, we adjust the assignment; for non-circulators, we start after shift
        if (!routeAssignment.route.isCirculator) {
          // Non-circulator: route starts after previous shift ends
          routeStartTime = currentShiftEndTime;
        }
        // For circulators, we'll handle the split to start at the right time
      }

      // Check if this route assignment fits in current shift (after adjusting for sequential scheduling)
      const adjustedDuration = calculateTimeDifference(routeStartTime, end);
      if (currentShiftDuration + adjustedDuration <= availableShiftTime && 
          (currentShiftEndTime === null || routeStartTime >= currentShiftEndTime)) {
        // Fits and doesn't overlap: add to current shift
        // Adjust route assignment start time if needed
        const adjustedAssignment: RouteAssignmentTime = {
          ...routeAssignment,
          startTime: formatTime(routeStartTime),
        };
        currentShift.push(adjustedAssignment);
        currentShiftDuration += adjustedDuration;
        currentShiftEndTime = end > (currentShiftEndTime || 0) ? end : currentShiftEndTime;
      } else {
        // Doesn't fit or overlaps: start new shift
        if (currentShift.length > 0) {
          shiftGroups.push(currentShift);
        }
        currentShift = [];
        currentShiftDuration = 0;
        currentShiftEndTime = null;

        // Check if route assignment itself exceeds shift time
        if (duration > availableShiftTime) {
          // Route assignment exceeds shift time
          // For circulators: split at loop boundaries (terminus points) only
          // For non-circulators: complete route goes in its own shift (routes never split)
          if (routeAssignment.route.isCirculator && routeAssignment.loopNumbers && routeAssignment.loopNumbers.length > 0) {
            // Circulator: split at loop boundaries using maxShiftTime (breaks scheduled within shift)
            const adjustedRouteAssignment: RouteAssignmentTime = currentShiftEndTime !== null && routeStartTime < currentShiftEndTime
              ? {
                  ...routeAssignment,
                  startTime: formatTime(currentShiftEndTime),
                }
              : routeAssignment;
            
            const splitAssignments = this.splitCirculatorAtTerminus(adjustedRouteAssignment, this.config.maxShiftTimeMinutes);
            
            // Add first split to current shift
            if (splitAssignments.length > 0) {
              const firstSplit = splitAssignments[0];
              const firstDuration = calculateTimeDifference(
                parseTime(firstSplit.startTime),
                parseTime(firstSplit.endTime)
              );
              currentShift.push(firstSplit);
              currentShiftDuration = firstDuration;
              currentShiftEndTime = parseTime(firstSplit.endTime);
              
              // Remaining splits each go into their own shifts
              for (let i = 1; i < splitAssignments.length; i++) {
                if (currentShift.length > 0) {
                  shiftGroups.push(currentShift);
                }
                const splitAssignment = splitAssignments[i];
                const splitDuration = calculateTimeDifference(
                  parseTime(splitAssignment.startTime),
                  parseTime(splitAssignment.endTime)
                );
                currentShift = [splitAssignment];
                currentShiftDuration = splitDuration;
                currentShiftEndTime = parseTime(splitAssignment.endTime);
              }
            } else {
              // No splits possible - route too long, assign to own shift (will warn later)
              currentShift.push(adjustedRouteAssignment);
              currentShiftDuration = duration;
              currentShiftEndTime = end;
            }
          } else {
            // Non-circulator: complete route goes in its own shift (routes never split)
            // Adjust start time to be sequential if needed
            const adjustedRouteAssignment: RouteAssignmentTime = currentShiftEndTime !== null && routeStartTime < currentShiftEndTime
              ? {
                  ...routeAssignment,
                  startTime: formatTime(currentShiftEndTime),
                  endTime: formatTime(addMinutes(currentShiftEndTime, duration)),
                }
              : routeAssignment;
            currentShift.push(adjustedRouteAssignment);
            currentShiftDuration = duration;
            currentShiftEndTime = parseTime(adjustedRouteAssignment.endTime);
          }
        } else {
          // Route fits - use adjusted start time if needed
          const adjustedRouteAssignment: RouteAssignmentTime = currentShiftEndTime !== null && routeStartTime < currentShiftEndTime
            ? {
                ...routeAssignment,
                startTime: formatTime(currentShiftEndTime),
                endTime: formatTime(addMinutes(currentShiftEndTime, duration)),
              }
            : routeAssignment;
          currentShift.push(adjustedRouteAssignment);
          currentShiftDuration = duration;
          currentShiftEndTime = parseTime(adjustedRouteAssignment.endTime);
        }
      }
    }

    // Add final shift if it has assignments
    if (currentShift.length > 0) {
      shiftGroups.push(currentShift);
    }

    return shiftGroups.length > 0 ? shiftGroups : [[]];
  }

  /**
   * Split route assignment - DEPRECATED
   * 
   * Routes should never be split. For circulators, use splitCirculatorAtTerminus.
   * For non-circulators, complete routes go in their own shifts.
   * 
   * @deprecated Use splitCirculatorAtTerminus for circulators, or assign complete routes to shifts
   */
  private splitRouteAssignment(
    routeAssignment: RouteAssignmentTime,
    maxShiftTime: number
  ): RouteAssignmentTime[] {
    // Routes should never be split - return original
    return [routeAssignment];
  }

  /**
   * Split circulator route at terminus points (loop boundaries)
   * 
   * This ensures routes are never split mid-route. Shifts can change
   * at the terminus (completion of a loop), creating new route assignments
   * for each group of complete loops.
   * 
   * @param routeAssignment - Circulator route assignment to split
   * @param maxShiftTime - Maximum shift time in minutes (full shift duration, breaks are scheduled within)
   * @returns Array of route assignments, each containing complete loops
   */
  private splitCirculatorAtTerminus(
    routeAssignment: RouteAssignmentTime,
    maxShiftTime: number
  ): RouteAssignmentTime[] {
    const route = routeAssignment.route;
    const loopNumbers = routeAssignment.loopNumbers || [];

    if (loopNumbers.length === 0) {
      // No loops defined, can't split - return original
      return [routeAssignment];
    }

    // Calculate loop duration
    const loopDuration = calculateRouteLoopDuration(route, this.trafficMultiplier);
    
    if (loopDuration <= 0) {
      // Invalid loop duration, return original
      return [routeAssignment];
    }

    // Calculate how many complete loops fit in one shift
    // Use full maxShiftTime - breaks are scheduled within the shift, not subtracted upfront
    const loopsPerShift = Math.floor(maxShiftTime / loopDuration);
    
    if (loopsPerShift <= 0) {
      // Shift time too short for even one complete loop
      // Return original - will trigger validation warning but allow route delivery
      return [routeAssignment];
    }

    const startMinutes = parseTime(routeAssignment.startTime);

    // Split loops into groups of complete loops (at terminus boundaries)
    const splits: RouteAssignmentTime[] = [];
    let currentLoopIndex = 0;
    let currentShiftStart = startMinutes;

    while (currentLoopIndex < loopNumbers.length) {
      // Take up to loopsPerShift complete loops for this shift
      const loopsForThisShift = Math.min(loopsPerShift, loopNumbers.length - currentLoopIndex);
      const loopGroup = loopNumbers.slice(currentLoopIndex, currentLoopIndex + loopsForThisShift);
      
      // Calculate shift duration based on complete loops
      const shiftDuration = loopsForThisShift * loopDuration;
      const shiftEnd = addMinutes(currentShiftStart, shiftDuration);

      // Create route assignment for this group of complete loops
      // Shift ends at terminus (after completing last loop)
      splits.push({
        route: route,
        startTime: formatTime(currentShiftStart),
        endTime: formatTime(shiftEnd),
        loopNumbers: loopGroup,
        startStopId: routeAssignment.startStopId,
        endStopId: routeAssignment.endStopId,
      });

      currentLoopIndex += loopsForThisShift;
      currentShiftStart = shiftEnd; // Next shift starts at terminus (after completing last loop)
    }

    return splits.length > 0 ? splits : [routeAssignment];
  }

  /**
   * DEPRECATED: Non-circulator routes should never be split
   * 
   * Non-circulator routes are always complete routes. If a route exceeds
   * max shift time, it goes in its own shift (complete route only).
   * 
   * @deprecated Non-circulators are never split - complete routes go in shifts
   */
  private splitNonCirculatorRoute(
    routeAssignment: RouteAssignmentTime,
    maxShiftTime: number
  ): RouteAssignmentTime[] {
    // Non-circulator routes should never be split - return original
    // Complete route goes in its own shift even if it exceeds max shift time
    return [routeAssignment];
  }

  /**
   * Create shift periods from consolidated assignments
   * 
   * For each shift group, creates a VehicleAssignment with route assignments
   * and scheduled breaks. Each shift becomes a separate VehicleAssignment
   * (though they share the same vehicle ID/name).
   * 
   * @param originalAssignment - Original vehicle assignment
   * @param consolidatedAssignments - Route assignments organized into shift groups
   * @param vehicleIndex - Vehicle index for break staggering
   * @returns Array of vehicle assignments (one per shift)
   */
  private createShiftPeriods(
    originalAssignment: VehicleAssignment,
    consolidatedAssignments: RouteAssignmentTime[][],
    vehicleIndex: number
  ): VehicleAssignment[] {
    const shiftAssignments: VehicleAssignment[] = [];

    for (let shiftIndex = 0; shiftIndex < consolidatedAssignments.length; shiftIndex++) {
      const shiftRouteAssignments = consolidatedAssignments[shiftIndex];
      
      if (shiftRouteAssignments.length === 0) {
        continue; // Skip empty shifts
      }

      // Calculate shift start and end times from route assignments
      // For split routes, each split already has its own time boundaries
      // The shift should span from first assignment start to last assignment end
      // Since routes in a shift should be sequential (no overlaps), we can safely use min/max
      let shiftStartMinutes = parseTime(shiftRouteAssignments[0].startTime);
      let shiftEndMinutes = parseTime(shiftRouteAssignments[0].endTime);

      // Find earliest start and latest end across all route assignments in this shift
      // For properly scheduled routes (sequential, no overlaps), this is straightforward
      for (const routeAssignment of shiftRouteAssignments) {
        const start = parseTime(routeAssignment.startTime);
        const end = parseTime(routeAssignment.endTime);
        
        // Start time: earliest start
        if (start < shiftStartMinutes) {
          shiftStartMinutes = start;
        }
        
        // End time: latest end
        // Handle overnight periods correctly
        if (end >= shiftStartMinutes) {
          // Normal case: end is after start (same day)
          if (end > shiftEndMinutes) {
            shiftEndMinutes = end;
          }
        } else {
          // Overnight case: end is before start (crosses midnight)
          const endNormalized = end + (24 * 60);
          const shiftEndNormalized = shiftEndMinutes < shiftStartMinutes 
            ? shiftEndMinutes + (24 * 60)
            : shiftEndMinutes;
          if (endNormalized > shiftEndNormalized) {
            shiftEndMinutes = end;
          }
        }
      }
      
      // Ensure shift boundaries are correct: end must be >= start
      // If we have a single route assignment (typical for split circulators), 
      // use its exact boundaries
      if (shiftRouteAssignments.length === 1) {
        const singleAssignment = shiftRouteAssignments[0];
        shiftStartMinutes = parseTime(singleAssignment.startTime);
        shiftEndMinutes = parseTime(singleAssignment.endTime);
      }
      
      // Note: The shift duration is calculated from route assignment times
      // Breaks are scheduled BETWEEN or around route assignments, so the total shift duration
      // will be route time + breaks. We'll validate this after breaks are scheduled.

      // Schedule breaks for this shift
      const breakSchedules = this.scheduleBreaksForShift(
        shiftRouteAssignments,
        shiftStartMinutes,
        shiftEndMinutes,
        vehicleIndex
      );

      // Validate shift duration (5.1.3.1)
      // Note: Shift duration includes both route assignments AND breaks
      // The shift can be up to maxShiftTimeMinutes total (routes + breaks)
      const shiftDuration = calculateTimeDifference(shiftStartMinutes, shiftEndMinutes);
      const totalBreakDuration = breakSchedules.reduce((sum, b) => sum + b.durationMinutes, 0);
      const routeDuration = shiftDuration - totalBreakDuration;
      
      // The shift duration (routes + breaks) should not exceed max shift time
      // However, if a single route/loop exceeds max shift time, we allow it (unavoidable)
      // but log a warning instead of throwing an error to allow the fleet to be generated
      if (shiftDuration > this.config.maxShiftTimeMinutes) {
        // Check if this is due to a single route assignment that can't be split further
        const isSingleLongRoute = shiftRouteAssignments.length === 1 && 
          calculateTimeDifference(
            parseTime(shiftRouteAssignments[0].startTime),
            parseTime(shiftRouteAssignments[0].endTime)
          ) > this.config.maxShiftTimeMinutes;
        
        if (isSingleLongRoute) {
          // This is unavoidable - route/loop is longer than max shift time
          // Allow it but warn the user
          console.warn(
            `Shift ${shiftIndex + 1} for vehicle ${originalAssignment.vehicleName} ` +
            `exceeds maximum shift time (${shiftDuration} min > ${this.config.maxShiftTimeMinutes} min) ` +
            `because a single route loop/assignment is longer than the maximum shift time. ` +
            `This may violate labor regulations. Consider adjusting route duration or shift constraints.`
          );
          // Continue processing - don't throw error
        } else {
          // This indicates a splitting/consolidation error
          throw new Error(
            `Shift duration (${shiftDuration} minutes, including ${totalBreakDuration} minutes of breaks) exceeds maximum shift time (${this.config.maxShiftTimeMinutes} minutes) ` +
            `for vehicle ${originalAssignment.vehicleName}, shift ${shiftIndex + 1}. ` +
            `This may indicate a routing/splitting error. Route assignments should have been split across multiple shifts.`
          );
        }
      }

      // Validate breaks don't interrupt route assignments (5.1.3.2)
      if (!this.validateBreaksNotDuringRoutes(breakSchedules, shiftRouteAssignments)) {
        throw new Error(
          `Breaks interrupt route assignments for vehicle ${originalAssignment.vehicleName}, shift ${shiftIndex + 1}. ` +
          `This indicates a scheduling error.`
        );
      }

      // Convert break schedules to BreakPeriod format (HH:MM strings)
      const breakPeriods = breakSchedules.map(breakSchedule => ({
        start: formatTime(breakSchedule.startMinutes),
        end: formatTime(breakSchedule.endMinutes),
        location: '', // No specific break location
      }));
      
      // Debug: Log breaks if they exist
      if (breakPeriods.length > 0) {
        console.log(
          `[Shift Allocator] Vehicle ${originalAssignment.vehicleName}, Shift ${shiftIndex + 1}: ` +
          `${breakPeriods.length} break(s) scheduled:`,
          breakPeriods.map(b => `${b.start}-${b.end}`).join(', ')
        );
      }

      // Create vehicle assignment for this shift
      // Store breaks in a way that fleetConverter can access them
      // We'll add them as a property that can be read during conversion
      const shiftAssignment: VehicleAssignment & { shiftBreaks?: Array<{ start: string; end: string; location: string }> } = {
        vehicleId: originalAssignment.vehicleId,
        vehicleName: consolidatedAssignments.length > 1
          ? `${originalAssignment.vehicleName} - Shift ${shiftIndex + 1}`
          : originalAssignment.vehicleName,
        routeAssignments: shiftRouteAssignments,
        operatingPeriod: {
          startTime: formatTime(shiftStartMinutes),
          endTime: formatTime(shiftEndMinutes),
        },
        // Store breaks that will be added to OperatingPeriod in fleetConverter
        shiftBreaks: breakPeriods,
      };

      shiftAssignments.push(shiftAssignment);
    }

    return shiftAssignments.length > 0 ? shiftAssignments : [originalAssignment];
  }

  /**
   * Schedule breaks for a shift
   * 
   * Schedules breaks between route assignments (not during route execution).
   * Ensures breaks meet timing requirements and don't interrupt routes.
   * Breaks are calculated based on actual service time (sum of route durations),
   * not shift duration (which includes gaps).
   * 
   * @param routeAssignments - Route assignments in this shift
   * @param shiftStart - Shift start time in minutes
   * @param shiftEnd - Shift end time in minutes
   * @param vehicleIndex - Vehicle index for staggering
   * @returns Array of break schedules (in minutes format)
   */
  private scheduleBreaksForShift(
    routeAssignments: RouteAssignmentTime[],
    shiftStart: number,
    shiftEnd: number,
    vehicleIndex: number
  ): Array<{ startMinutes: number; endMinutes: number; durationMinutes: number }> {
    // Calculate actual service time (sum of route assignment durations, excluding gaps)
    let totalServiceTime = 0;
    routeAssignments.forEach(assignment => {
      const start = parseTime(assignment.startTime);
      const end = parseTime(assignment.endTime);
      totalServiceTime += calculateTimeDifference(start, end);
    });

    const shiftDuration = calculateTimeDifference(shiftStart, shiftEnd);
    console.log(`[Shift Allocator] Break calculation for shift ${shiftStart}-${shiftEnd} (duration: ${shiftDuration} min):`);
    console.log(`  - Route assignments: ${routeAssignments.length}`);
    console.log(`  - Total service time: ${totalServiceTime} min`);
    console.log(`  - Gap time: ${shiftDuration - totalServiceTime} min`);
    console.log(`  - Using SERVICE TIME (${totalServiceTime} min) for break calculation instead of shift duration (${shiftDuration} min)`);

    // Calculate breaks based on SERVICE TIME, not shift duration (which includes gaps)
    const numBreaks = calculateRequiredBreaks(totalServiceTime, shiftStart, this.config);
    console.log(`  - Breaks required: ${numBreaks} (based on service time)`);

    if (numBreaks === 0) {
      return [];
    }

    // Get break schedules (in minutes format)
    // Note: We still use shiftStart/shiftEnd for scheduling, but breaks are calculated based on service time
    let breakSchedules: Array<{ startMinutes: number; endMinutes: number; durationMinutes: number }>;
    
    if (this.staggerBreaks) {
      // For staggered breaks, use service time duration instead of shift duration
      // Calculate shift end based on service time for staggering calculations
      const serviceBasedShiftEnd = shiftStart + totalServiceTime;
      breakSchedules = findStaggeredBreakTimes(shiftStart, totalServiceTime, vehicleIndex, this.config);
    } else {
      // For non-staggered breaks, we need to schedule them based on service time
      // But we still need to place them within the actual shift window
      // Calculate a virtual shift end based on service time for break scheduling
      const serviceBasedShiftEnd = shiftStart + totalServiceTime;
      breakSchedules = scheduleBreaks(shiftStart, serviceBasedShiftEnd, numBreaks, this.config);
    }

    // Filter breaks based on minimum service time requirements
    const assignmentsForFiltering = routeAssignments.map(ra => ({
      startTime: ra.startTime,
      endTime: ra.endTime,
    }));
    breakSchedules = this.filterBreaksByServiceTime(breakSchedules, assignmentsForFiltering);
    
    console.log(`[Shift Allocator] After filtering: ${breakSchedules.length} break(s) remain out of ${numBreaks} calculated`);

    // Ensure breaks don't interrupt route assignments
    // Adjust break times to fit between route assignments if needed
    // For circulator routes, breaks can occur during the route at terminus points
    return this.adjustBreaksBetweenRoutes(breakSchedules, routeAssignments);
  }

  /**
   * Adjust break times to ensure they occur between route assignments, not during
   * 
   * Only places breaks in gaps if they meet minimum time requirements:
   * - At least minTimeBeforeBreak minutes of service time since shift start or last break
   * - At least minTimeAfterBreak minutes of service time remaining after the break
   * 
   * @param breakSchedules - Break schedules in minutes
   * @param routeAssignments - Route assignments in this shift
   * @returns Adjusted break schedules
   */
  private adjustBreaksBetweenRoutes(
    breakSchedules: Array<{ startMinutes: number; endMinutes: number; durationMinutes: number }>,
    routeAssignments: RouteAssignmentTime[]
  ): Array<{ startMinutes: number; endMinutes: number; durationMinutes: number }> {
    if (routeAssignments.length === 0) {
      return breakSchedules;
    }

    // Sort route assignments by start time
    const sortedAssignments = [...routeAssignments].sort((a, b) => {
      const aStart = parseTime(a.startTime);
      const bStart = parseTime(b.startTime);
      return aStart - bStart;
    });

    // Calculate total service time (sum of route assignment durations, excluding gaps)
    let totalServiceTime = 0;
    sortedAssignments.forEach(assignment => {
      const start = parseTime(assignment.startTime);
      const end = parseTime(assignment.endTime);
      totalServiceTime += calculateTimeDifference(start, end);
    });

    // Find gaps between route assignments where breaks can fit
    const gaps: Array<{ start: number; end: number; serviceTimeBefore: number }> = [];
    
    let cumulativeServiceTime = 0;
    for (let i = 0; i < sortedAssignments.length - 1; i++) {
      const currentEnd = parseTime(sortedAssignments[i].endTime);
      const nextStart = parseTime(sortedAssignments[i + 1].startTime);
      const gapDuration = calculateTimeDifference(currentEnd, nextStart);
      
      // Add service time for current route assignment
      const currentStart = parseTime(sortedAssignments[i].startTime);
      cumulativeServiceTime += calculateTimeDifference(currentStart, currentEnd);
      
      if (gapDuration > 0) {
        gaps.push({ 
          start: currentEnd, 
          end: nextStart,
          serviceTimeBefore: cumulativeServiceTime
        });
      }
    }

    // Filter breaks to only those that meet minimum time requirements
    const adjustedBreaks: Array<{ startMinutes: number; endMinutes: number; durationMinutes: number }> = [];
    const shiftStart = parseTime(sortedAssignments[0].startTime);
    const shiftEnd = parseTime(sortedAssignments[sortedAssignments.length - 1].endTime);
    
    for (const breakSchedule of breakSchedules) {
      const breakDuration = breakSchedule.durationMinutes;
      const breakStart = breakSchedule.startMinutes;
      const breakEnd = breakSchedule.endMinutes;
      
      // Calculate service time before this break
      let serviceTimeBefore = 0;
      for (const assignment of sortedAssignments) {
        const assignmentStart = parseTime(assignment.startTime);
        const assignmentEnd = parseTime(assignment.endTime);
        
        if (assignmentEnd <= breakStart) {
          // This assignment ends before or at break start
          serviceTimeBefore += calculateTimeDifference(assignmentStart, assignmentEnd);
        } else if (assignmentStart < breakStart) {
          // Break occurs during this assignment (shouldn't happen, but handle it)
          serviceTimeBefore += calculateTimeDifference(assignmentStart, breakStart);
          break;
        } else {
          // Assignment starts after break
          break;
        }
      }
      
      // Calculate service time after this break
      let serviceTimeAfter = 0;
      for (let i = sortedAssignments.length - 1; i >= 0; i--) {
        const assignment = sortedAssignments[i];
        const assignmentStart = parseTime(assignment.startTime);
        const assignmentEnd = parseTime(assignment.endTime);
        
        if (assignmentStart >= breakEnd) {
          // This assignment starts after break ends
          serviceTimeAfter += calculateTimeDifference(assignmentStart, assignmentEnd);
        } else if (assignmentEnd > breakEnd) {
          // Break ends during this assignment (shouldn't happen, but handle it)
          serviceTimeAfter += calculateTimeDifference(breakEnd, assignmentEnd);
          break;
        } else {
          // Assignment ends before break
          break;
        }
      }
      
      // Check if break meets minimum time requirements
      if (serviceTimeBefore >= this.config.minTimeBeforeBreakMinutes &&
          serviceTimeAfter >= this.config.minTimeAfterBreakMinutes) {
        
        // Try to place break in a gap if possible
        if (gaps.length > 0) {
          const suitableGap = gaps.find(gap => {
            const gapDuration = calculateTimeDifference(gap.start, gap.end);
            // Check if gap can accommodate break AND meets service time requirements
            return gapDuration >= breakDuration && 
                   gap.serviceTimeBefore >= this.config.minTimeBeforeBreakMinutes &&
                   (totalServiceTime - gap.serviceTimeBefore) >= this.config.minTimeAfterBreakMinutes;
          });

          if (suitableGap) {
            // Place break at the start of the gap
            adjustedBreaks.push({
              startMinutes: suitableGap.start,
              endMinutes: addMinutes(suitableGap.start, breakDuration),
              durationMinutes: breakDuration,
            });
            continue;
          }
        }
        
        // No suitable gap found, but break meets requirements
        // For circulator routes, breaks can occur during the route at terminus points
        // For non-circulator routes, we still keep the break if it meets requirements
        // (it will be validated later in validateBreaksNotDuringRoutes)
        if (sortedAssignments.length === 1 && sortedAssignments[0].route.isCirculator) {
          // Single circulator route - breaks can occur during the route
          adjustedBreaks.push(breakSchedule);
        } else {
          // Multiple routes or non-circulator - keep original schedule if it meets requirements
          adjustedBreaks.push(breakSchedule);
        }
      }
      // If break doesn't meet requirements, skip it (don't add to adjustedBreaks)
    }

    return adjustedBreaks;
  }

  /**
   * Filter breaks based on minimum service time requirements
   * 
   * Only keeps breaks that meet:
   * - At least minTimeBeforeBreak minutes of service time before the break
   * - At least minTimeAfterBreak minutes of service time after the break
   * 
   * @param breaks - Break schedules in minutes
   * @param assignments - Array of assignments with startTime and endTime properties
   * @returns Filtered break schedules
   */
  private filterBreaksByServiceTime(
    breaks: Array<{ startMinutes: number; endMinutes: number; durationMinutes: number }>,
    assignments: Array<{ startTime: string; endTime: string }>
  ): Array<{ startMinutes: number; endMinutes: number; durationMinutes: number }> {
    if (assignments.length === 0) {
      return breaks;
    }

    // Sort assignments by start time
    const sortedAssignments = [...assignments].sort((a, b) => {
      const aStart = parseTime(a.startTime);
      const bStart = parseTime(b.startTime);
      return aStart - bStart;
    });

    // Calculate total service time
    let totalServiceTime = 0;
    sortedAssignments.forEach(assignment => {
      const start = parseTime(assignment.startTime);
      const end = parseTime(assignment.endTime);
      totalServiceTime += calculateTimeDifference(start, end);
    });

    // Filter breaks based on service time requirements
    const filteredBreaks: Array<{ startMinutes: number; endMinutes: number; durationMinutes: number }> = [];

    for (const breakSchedule of breaks) {
      const breakStart = breakSchedule.startMinutes;
      const breakEnd = breakSchedule.endMinutes;

      // Calculate service time before this break
      let serviceTimeBefore = 0;
      for (const assignment of sortedAssignments) {
        const assignmentStart = parseTime(assignment.startTime);
        const assignmentEnd = parseTime(assignment.endTime);

        if (assignmentEnd <= breakStart) {
          // This assignment ends before or at break start
          serviceTimeBefore += calculateTimeDifference(assignmentStart, assignmentEnd);
        } else if (assignmentStart < breakStart) {
          // Break occurs during this assignment (shouldn't happen, but handle it)
          serviceTimeBefore += calculateTimeDifference(assignmentStart, breakStart);
          break;
        } else {
          // Assignment starts after break
          break;
        }
      }

      // Calculate service time after this break
      let serviceTimeAfter = 0;
      for (let i = sortedAssignments.length - 1; i >= 0; i--) {
        const assignment = sortedAssignments[i];
        const assignmentStart = parseTime(assignment.startTime);
        const assignmentEnd = parseTime(assignment.endTime);

        if (assignmentStart >= breakEnd) {
          // This assignment starts after break ends
          serviceTimeAfter += calculateTimeDifference(assignmentStart, assignmentEnd);
        } else if (assignmentEnd > breakEnd) {
          // Break ends during this assignment (shouldn't happen, but handle it)
          serviceTimeAfter += calculateTimeDifference(breakEnd, assignmentEnd);
          break;
        } else {
          // Assignment ends before break
          break;
        }
      }

      // Only keep breaks that meet minimum time requirements
      if (serviceTimeBefore >= this.config.minTimeBeforeBreakMinutes &&
          serviceTimeAfter >= this.config.minTimeAfterBreakMinutes) {
        filteredBreaks.push(breakSchedule);
      } else {
        console.log(`[Shift Allocator] Filtering out break ${formatTime(breakStart)}-${formatTime(breakEnd)}: serviceTimeBefore=${serviceTimeBefore} min (need ${this.config.minTimeBeforeBreakMinutes}), serviceTimeAfter=${serviceTimeAfter} min (need ${this.config.minTimeAfterBreakMinutes})`);
      }
    }

    return filteredBreaks;
  }

  /**
   * Validate that a shift duration respects the maximum shift time constraint
   * 
   * @param shiftStart - Shift start time in minutes
   * @param shiftEnd - Shift end time in minutes
   * @param maxShiftTime - Maximum shift time in minutes
   * @returns True if shift duration is valid
   */
  validateShiftDuration(
    shiftStart: number,
    shiftEnd: number,
    maxShiftTime: number
  ): boolean {
    const shiftDuration = calculateTimeDifference(shiftStart, shiftEnd);
    return shiftDuration <= maxShiftTime;
  }

  /**
   * Validate that breaks don't occur during route assignments
   * 
   * @param breaks - Array of break schedules (in minutes format)
   * @param routeAssignments - Route assignments to check against
   * @returns True if all breaks occur between route assignments (not during)
   */
  validateBreaksNotDuringRoutes(
    breaks: Array<{ startMinutes: number; endMinutes: number }>,
    routeAssignments: RouteAssignmentTime[]
  ): boolean {
    for (const breakPeriod of breaks) {
      for (const routeAssignment of routeAssignments) {
        const routeStart = parseTime(routeAssignment.startTime);
        const routeEnd = parseTime(routeAssignment.endTime);

        // Check if break overlaps with route assignment
        // Break overlaps if: breakStart < routeEnd AND breakEnd > routeStart
        if (
          breakPeriod.startMinutes < routeEnd &&
          breakPeriod.endMinutes > routeStart
        ) {
          // For circulator routes, breaks can occur during the route at terminus points
          // (completion of loops). This is acceptable for continuous circulator operations.
          if (routeAssignment.route.isCirculator) {
            // Allow breaks during circulator routes - they occur at terminus points
            // (between loop completions)
            continue;
          }
          
          // For non-circulator routes, breaks should not interrupt route execution
          // Check if break is fully contained within route or overlaps
          const breakFullyInRoute = 
            breakPeriod.startMinutes >= routeStart && 
            breakPeriod.endMinutes <= routeEnd;
          
          const breakOverlapsRoute =
            (breakPeriod.startMinutes < routeStart && breakPeriod.endMinutes > routeStart) ||
            (breakPeriod.startMinutes < routeEnd && breakPeriod.endMinutes > routeEnd) ||
            breakFullyInRoute;
          
          if (breakOverlapsRoute) {
            return false; // Break interrupts non-circulator route assignment
          }
        }
      }
    }
    return true; // All breaks are valid (between routes or at circulator terminus points)
  }

  /**
   * Validate that shifts don't overlap for the same vehicle
   * 
   * @param periods - Array of operating periods (shifts) to check
   * @returns True if shifts don't overlap
   */
  validateShiftsDontOverlap(
    periods: Array<{ startTime: string; endTime: string }>
  ): boolean {
    if (periods.length <= 1) {
      return true; // Single or no periods - no overlap possible
    }

    // Sort periods by start time
    const sorted = [...periods].sort((a, b) => {
      const aStart = parseTime(a.startTime);
      const bStart = parseTime(b.startTime);
      return aStart - bStart;
    });

    // Check adjacent periods for overlap
    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];
      
      const currentStart = parseTime(current.startTime);
      const currentEnd = parseTime(current.endTime);
      const nextStart = parseTime(next.startTime);
      const nextEnd = parseTime(next.endTime);

      // Calculate gap between shifts using time difference utility
      // This properly handles overnight periods and time wrapping
      const gap = calculateTimeDifference(currentEnd, nextStart);
      
      // Gap < 0 means shifts overlap (next starts before current ends)
      // Gap == 0 means shifts are adjacent (acceptable - one ends exactly when next starts)
      // Gap > 0 means shifts are separated (acceptable)
      if (gap < 0) {
        return false; // Shifts overlap
      }
    }

    return true; // No overlaps found
  }
}

